import { PropsWithChildren, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { PATH_LOGIN, PATH_ONBOARDING } from "@routes/paths";
import { useAuthStore } from "@/store/auth/auth.store";
import { useRolesStore } from "@/store/roles/roles.store";
import Loader from "@/components/Loader";

type Props = PropsWithChildren;

const AuthGuard: React.FC<Props> = ({ children }) => {
  const initialized = useAuthStore((s) => s.initialized);
  const user = useAuthStore((s) => s.user);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const rolesInitialized = useRolesStore((s) => s.initialized);
  const { pathname, search } = useLocation();
  const isOnboardingPage = pathname.startsWith(PATH_ONBOARDING);

  useEffect(() => {
    if (!initialized) useAuthStore.getState().initializeApp();
    if (isLoggedIn && !user.id) useAuthStore.getState().getProfile();
  }, [initialized, isLoggedIn, user.id]);

  useEffect(() => {
    // Load the roles catalog as soon as we have a logged-in user so permission
    // checks (hasPermission) resolve correctly across the app.
    if (isLoggedIn && user.id && !rolesInitialized) {
      useRolesStore.getState().getAllRoles();
    }
  }, [isLoggedIn, user.id, rolesInitialized]);

  // Request permission to show notifications
  useEffect(() => {
    if (isLoggedIn && user.id && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [isLoggedIn, user.id]);

  if (!isLoggedIn) {
    // Don't redirect if already on login page
    if (pathname !== PATH_LOGIN) {
      // Build the full path with search params if they exist
      const fullPath = pathname + search;
      // Encode the full path as a query parameter
      const loginUrl = `${PATH_LOGIN}?redirect=${encodeURIComponent(fullPath)}`;
      return <Navigate to={loginUrl} replace />;
    }
  }

  if (isLoggedIn && !user.id) return <Loader />;
  // Wait for the roles catalog before rendering protected content so that
  // permission-guarded routes/UI don't flash 403 during bootstrap.
  if (isLoggedIn && user.id && !rolesInitialized) return <Loader />;

  if (isLoggedIn && user?.onboarding && !isOnboardingPage) return <Navigate to={PATH_ONBOARDING} />;

  return <>{children}</>;
};

export default AuthGuard;
